import {
  FIELD_TYPES,
  classifyFields,
  formatFieldLabel,
  getMeaningfulUniqueValues,
  isMeaningfulValue,
  isPrimitiveScalar,
  normalizeCategoryValue,
  normalizeFieldName,
} from "./visualizationData";
import { ORGANIZATION_CHART_COLORS } from "./visualizationColors";

export const RANKING_PRIMARY_COLOR = ORGANIZATION_CHART_COLORS[0];
export const RANKING_METRIC_UNRESOLVED_MESSAGE =
  "Unable to determine which value was used for this ranking.";
export const RANKING_DUPLICATE_MESSAGE =
  "This ranking contains multiple indistinguishable records for the same ranked entity.";
export const RANKING_ENTITY_UNRESOLVED_MESSAGE =
  "Unable to determine which entity was ranked in this result.";
export const RANKING_PARENT_UNRESOLVED_MESSAGE =
  "Unable to determine the parent grouping for this ranking.";
export const RANKING_FIGURE_UNRESOLVED_MESSAGE =
  "Unable to identify each ranked observation in this grouped ranking.";

const RANK_LEVELS = new Map([
  ["overall", "overall"],
  ["global", "overall"],
  ["regular", "overall"],
  ["grouped_entity", "grouped_entity"],
  ["groupedentity", "grouped_entity"],
  ["entity_grouped", "grouped_entity"],
  ["grouped_figure", "grouped_figure"],
  ["groupedfigure", "grouped_figure"],
  ["figure_grouped", "grouped_figure"],
]);

const FIELD_ALIAS_GROUPS = {
  country: ["country", "country_name", "admin_0", "admin_0_name", "admin0", "admin0_name"],
  admin_1: ["admin_1", "admin_1_name", "admin1", "admin1_name", "admin_1_name_en"],
  admin_2: ["admin_2", "admin_2_name", "admin2", "admin2_name", "admin_2_name_en"],
  reason: ["reason", "reason_for_displacement", "displacement_reason"],
  origin: [
    "origin",
    "origin_name",
    "origin_admin_1",
    "origin_admin_1_name",
    "origin_admin1",
    "origin_admin1_name",
    "idp_origin_admin_1",
    "admin_1_origin_name",
    "admin1_origin_name",
  ],
  region: ["region", "region_name"],
  operation: ["operation", "operation_name"],
  observation: ["observation", "observation_label", "record_label", "display_label", "label"],
};

const FIELD_ALIAS_LOOKUP = Object.entries(FIELD_ALIAS_GROUPS).reduce(
  (lookup, [canonical, aliases]) => {
    aliases.forEach((alias) => lookup.set(alias, canonical));
    return lookup;
  },
  new Map(),
);

const METRIC_ALIAS_GROUPS = {
  total_idps: [
    "total_idps",
    "num_present_idp_ind",
    "num_present_idp",
    "total_present_idps",
    "idp_caseload",
    "total",
  ],
  male_idps: [
    "male_idps",
    "num_present_idp_male",
    "num_present_idp_ind_male",
    "total_male_idps",
    "idps_male",
  ],
  female_idps: [
    "female_idps",
    "num_present_idp_female",
    "num_present_idp_ind_female",
    "total_female_idps",
    "idps_female",
  ],
};

const METRIC_ALIAS_LOOKUP = Object.entries(METRIC_ALIAS_GROUPS).reduce(
  (lookup, [canonical, aliases]) => {
    aliases.forEach((alias) => lookup.set(alias, canonical));
    return lookup;
  },
  new Map(),
);

const getCanonicalMetric = (field) => {
  const normalized = normalizeToken(field);
  return METRIC_ALIAS_LOOKUP.get(normalized) || normalized;
};

const getIdsMetricBaseIndicator = (response) => {
  const raw = response?.ids?.metric?.base_indicator;
  return isMeaningfulValue(raw) ? metadataScalar(raw) : null;
};

const resolveQueryResultColumnsMetric = (response) => {
  const columns = Array.isArray(response?.query_result_columns) ? response.query_result_columns : [];
  const metricColumns = columns.filter(
    (column) =>
      column &&
      typeof column === "object" &&
      normalizeToken(column.role) === "metric" &&
      column.key,
  );
  if (!metricColumns.length) return null;
  const families = new Set(metricColumns.map((column) => getCanonicalMetric(column.key)));
  if (families.size !== 1) return null;
  return metricColumns[0].key;
};

const RANK_FIELD_ALIASES = ["rank", "group_rank", "ranking", "rank_number", "row_rank", "position"];
const TEMPORAL_CONTEXT_PATTERN =
  /(?:^|_)(?:year|reporting_year|month|year_month|month_year|date|reporting_date|round|round_number)$/;
const INTERNAL_FIELD_PATTERN =
  /(?:^|_)(?:row_?id|source_?id|trace|debug|metadata|planner|candidate|selected_rows?|sql|query_trace|freshness_code|pcode|identifier)(?:_|$)/;
const NON_INFERRED_METRIC_PATTERN =
  /(?:^|_)(?:year|month|round|rank|ranking|position|freshness|id|identifier|code|pcode|pct|percent|percentage|rate|ratio|share|proportion|average|avg|mean|median|score|index)(?:_|$)/;
const RANKING_METRIC_METADATA_PATTERN =
  /(?:^|_)(?:requested_?anchor|anchor|lookback|source|reconciliation|trace|debug)(?:_|$)|(?:^|_)(?:operation_count|operations_count|num_operations)(?:_|$)/;

const normalizeToken = (value) =>
  normalizeFieldName(value).replace(/[\s-]+/g, "_");

const metadataScalar = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value !== "object" || Array.isArray(value)) return value;
  return (
    value.field ??
    value.key ??
    value.name ??
    value.metric ??
    value.value ??
    value.type ??
    null
  );
};

const collectFields = (rows) => {
  const fields = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) return;
    Object.keys(row).forEach((field) => fields.add(field));
  });
  return Array.from(fields);
};

const findMetadataValue = (sources, aliases) => {
  for (const source of sources) {
    if (!source || typeof source !== "object" || Array.isArray(source)) continue;
    for (const alias of aliases) {
      if (source[alias] !== undefined && source[alias] !== null && source[alias] !== "") {
        return metadataScalar(source[alias]);
      }
    }
  }
  return null;
};

const getAnalysisMarker = (response) => {
  const analysis = response?.response_type_analysis;
  return metadataScalar(analysis?.type ?? analysis);
};

const getRankingMetadataSourceEntries = (response) => [
  { path: "ranking_metadata", value: response?.ranking_metadata },
  { path: "ranking", value: response?.ranking },
  { path: "analysis_metadata.ranking", value: response?.analysis_metadata?.ranking },
  { path: "analysis_metadata", value: response?.analysis_metadata },
  { path: "visualization_config.ranking", value: response?.visualization_config?.ranking },
  { path: "response", value: response },
];

const getRankingMetadataSources = (response) =>
  getRankingMetadataSourceEntries(response).map(({ value }) => value);

const findMetadataEntry = (entries, aliases) => {
  for (const entry of entries) {
    const source = entry?.value;
    if (!source || typeof source !== "object" || Array.isArray(source)) continue;
    for (const alias of aliases) {
      if (source[alias] !== undefined && source[alias] !== null && source[alias] !== "") {
        return { path: entry.path, value: metadataScalar(source[alias]) };
      }
    }
  }
  return null;
};

export const normalizeRankLevel = (value) =>
  RANK_LEVELS.get(normalizeToken(metadataScalar(value))) || null;

const normalizeRankType = (value) => {
  const normalized = normalizeToken(metadataScalar(value));
  if (["top", "top_n", "highest", "maximum", "max"].includes(normalized)) return "top_n";
  if (["bottom", "bottom_n", "lowest", "minimum", "min"].includes(normalized)) return "bottom_n";
  return normalized || null;
};

export const isRankingResponse = (response) => {
  if (!response || typeof response !== "object") return false;
  const explicitMarkers = [
    getAnalysisMarker(response),
    response.response_type,
    response.analysis_type,
    response.query_type,
    response.visualization_config?.analysis_type,
  ].map((value) => normalizeToken(value));
  if (explicitMarkers.some((value) => value === "ranking" || value.includes("ranking"))) {
    return true;
  }
  return getRankingMetadataSources(response).some(
    (source) =>
      source &&
      typeof source === "object" &&
      ["rank_level", "rank_type", "ranked_entity", "rank_level_group", "ranking_metric"].some(
        (field) => source[field] !== undefined && source[field] !== null,
      ),
  );
};

export const resolveRankingMetadata = (response) => {
  const sourceEntries = getRankingMetadataSourceEntries(response);
  const parentEntry = findMetadataEntry(sourceEntries, [
    "rank_level_group",
    "ranking_group",
    "group_field",
    "parent_field",
    "grouped_by",
  ]);
  const rankLevelEntry = findMetadataEntry(
    sourceEntries,
    ["rank_level", "ranking_level", "level"],
  );
  const patternEntry = findMetadataEntry(
    sourceEntries,
    ["pattern", "ranking_pattern"],
  );
  const rankTypeEntry = findMetadataEntry(
    sourceEntries,
    ["rank_type", "ranking_type", "extreme_type"],
  );
  const rankedEntityEntry = findMetadataEntry(sourceEntries, [
    "ranked_entity",
    "ranking_entity",
    "entity_field",
    "ranked_field",
  ]);
  const rankLimitEntry = findMetadataEntry(
    sourceEntries,
    ["value_n", "rank_limit", "ranking_limit", "limit", "top_n"],
  );
  const parent = parentEntry?.value ?? null;
  const explicitLevel = normalizeRankLevel(rankLevelEntry?.value);
  // Production ranking contract: the ranking metric must be resolved from
  // ranking_metadata.ranking_metric first, then ranking_metadata.primary_metric,
  // before any other alias or row-field fallback is considered.
  const explicitRankingMetricEntry = findMetadataEntry(sourceEntries.slice(0, -1), [
    "ranking_metric",
    "primary_metric",
    "rank_metric",
    "ranking_metric_field",
    "metric_field",
  ]);
  const requestedMetricEntry = findMetadataEntry(sourceEntries, [
    "requested_metric",
    "requested_indicator",
    "metric",
    "indicator",
    "figure",
  ]);
  const idsMetric = getIdsMetricBaseIndicator(response);
  const queryResultColumnsMetric = resolveQueryResultColumnsMetric(response);
  return {
    pattern: patternEntry?.value ?? null,
    rankLevel: explicitLevel || (parent ? "grouped_entity" : "overall"),
    rankType: normalizeRankType(rankTypeEntry?.value),
    rankedEntity: rankedEntityEntry?.value ?? null,
    parent,
    valueN: rankLimitEntry?.value ?? null,
    rankLimit: rankLimitEntry?.value ?? null,
    explicitRankingMetric: explicitRankingMetricEntry?.value ?? null,
    explicitRankingMetricSource: explicitRankingMetricEntry?.path ?? null,
    requestedMetric: requestedMetricEntry?.value ?? null,
    requestedMetricSource: requestedMetricEntry?.path ?? null,
    idsMetric,
    idsMetricSource: idsMetric ? "ids.metric.base_indicator" : null,
    queryResultColumnsMetric,
    queryResultColumnsMetricSource: queryResultColumnsMetric ? "query_result_columns" : null,
    metadataSources: {
      pattern: patternEntry?.path ?? null,
      rankLevel: rankLevelEntry?.path ?? null,
      rankType: rankTypeEntry?.path ?? null,
      rankedEntity: rankedEntityEntry?.path ?? null,
      parent: parentEntry?.path ?? null,
      rankingMetric:
        explicitRankingMetricEntry?.path ??
        requestedMetricEntry?.path ??
        (idsMetric ? "ids.metric.base_indicator" : null) ??
        (queryResultColumnsMetric ? "query_result_columns" : null) ??
        null,
      valueN: rankLimitEntry?.path ?? null,
    },
    sequential: [
      getAnalysisMarker(response),
      response?.rank_type,
      response?.ranking_metadata?.rank_type,
    ].some((value) => normalizeToken(value).includes("sequential")),
  };
};

const resolveAliasedField = (rows, requested, aliasGroups, aliasLookup) => {
  const fields = collectFields(rows);
  const fieldByNormalized = new Map(fields.map((field) => [normalizeToken(field), field]));
  const normalizedRequest = normalizeToken(metadataScalar(requested));
  if (!normalizedRequest) return null;
  if (fieldByNormalized.has(normalizedRequest)) return fieldByNormalized.get(normalizedRequest);
  const canonical = aliasLookup.get(normalizedRequest) || normalizedRequest;
  const aliases = aliasGroups[canonical] || [];
  return aliases.map((alias) => fieldByNormalized.get(alias)).find(Boolean) || null;
};

const isInternalField = (field) => {
  const normalized = normalizeToken(field);
  return (
    INTERNAL_FIELD_PATTERN.test(normalized) ||
    /^(?:trace|debug|sql|planner|candidate|intermediate|execution)_/.test(normalized) ||
    /_(?:id|pcode|code)$/.test(normalized)
  );
};

const isRankFieldName = (field) => RANK_FIELD_ALIASES.includes(normalizeToken(field));
const isFiniteNumericField = (rows, field) =>
  rows.some((row) => typeof row?.[field] === "number" && Number.isFinite(row[field]));

const isDescriptiveField = (rows, field) =>
  !isInternalField(field) &&
  !isRankFieldName(field) &&
  rows.some((row) => {
    const value = row?.[field];
    return isMeaningfulValue(value) && isPrimitiveScalar(value) && typeof value !== "number";
  });

const getCanonicalEntity = (fieldOrAlias) =>
  FIELD_ALIAS_LOOKUP.get(normalizeToken(fieldOrAlias)) || normalizeToken(fieldOrAlias);

export const resolveRankedEntityField = ({ rows, metadata, parentField = null }) => {
  const explicit = resolveAliasedField(
    rows,
    metadata?.rankedEntity,
    FIELD_ALIAS_GROUPS,
    FIELD_ALIAS_LOOKUP,
  );
  if (metadata?.rankedEntity) return explicit;

  const fields = collectFields(rows).filter(
    (field) =>
      field !== parentField &&
      isDescriptiveField(rows, field) &&
      !TEMPORAL_CONTEXT_PATTERN.test(normalizeToken(field)) &&
      !["operation", "operation_name"].includes(normalizeToken(field)),
  );
  const varying = fields.filter((field) => getMeaningfulUniqueValues(rows, field).length > 1);
  const entityLikeVarying = varying.filter((field) => FIELD_ALIAS_LOOKUP.has(normalizeToken(field)));
  if (entityLikeVarying.length === 1) return entityLikeVarying[0];
  if (varying.length === 1) return varying[0];
  if (rows.length === 1) {
    const entityLike = fields.filter((field) => FIELD_ALIAS_LOOKUP.has(normalizeToken(field)));
    if (entityLike.length === 1) return entityLike[0];
    if (fields.length === 1) return fields[0];
  }
  return null;
};

export const resolveRankingParentField = ({ rows, metadata }) =>
  resolveAliasedField(rows, metadata?.parent, FIELD_ALIAS_GROUPS, FIELD_ALIAS_LOOKUP);

const resolveMetricAlias = (rows, requested) => {
  const fields = collectFields(rows).filter((field) => isFiniteNumericField(rows, field));
  const fieldByNormalized = new Map(fields.map((field) => [normalizeToken(field), field]));
  const normalizedRequest = normalizeToken(metadataScalar(requested));
  if (!normalizedRequest) return null;
  if (fieldByNormalized.has(normalizedRequest)) return fieldByNormalized.get(normalizedRequest);
  const canonical = METRIC_ALIAS_LOOKUP.get(normalizedRequest) || normalizedRequest;
  return (METRIC_ALIAS_GROUPS[canonical] || [])
    .map((alias) => fieldByNormalized.get(alias))
    .find(Boolean) || null;
};
const getPreferredMetricField = (canonical, fields) => {
  const fieldByNormalized = new Map(fields.map((field) => [normalizeToken(field), field]));
  if (fieldByNormalized.has(canonical)) return fieldByNormalized.get(canonical);
  return (METRIC_ALIAS_GROUPS[canonical] || [])
    .map((alias) => fieldByNormalized.get(alias))
    .find(Boolean) || fields[0] || null;
};


const getExcludedMetricReason = (field) => {
  const normalized = normalizeToken(field);
  if (isRankFieldName(field)) return "rank_context";
  if (isInternalField(field)) return "internal_metadata";
  if (RANKING_METRIC_METADATA_PATTERN.test(normalized)) return "response_metadata";
  if (NON_INFERRED_METRIC_PATTERN.test(normalized)) return "non_additive_context";
  return null;
};

const buildMetricCandidates = (rows) => {
  const classifications = new Map(
    classifyFields(rows).map((classification) => [classification.field, classification]),
  );
  const numericFields = collectFields(rows).filter((field) => isFiniteNumericField(rows, field));
  const excludedNumericFields = numericFields.flatMap((field) => {
    const reason = getExcludedMetricReason(field);
    return reason ? [{ field, reason }] : [];
  });
  const excluded = new Set(excludedNumericFields.map(({ field }) => field));
  const groups = new Map();
  numericFields.filter((field) => !excluded.has(field)).forEach((field) => {
    const canonical = getCanonicalMetric(field);
    const fields = groups.get(canonical) || [];
    fields.push(field);
    groups.set(canonical, fields);
  });
  const normalizedCandidates = Array.from(groups, ([canonical, fields]) => {
    const field = getPreferredMetricField(canonical, fields);
    return {
      canonical,
      fields,
      field,
      recognizedAdditive:
        Boolean(METRIC_ALIAS_GROUPS[canonical]) ||
        fields.some((candidate) =>
          classifications.get(candidate)?.type === FIELD_TYPES.ADDITIVE_METRIC),
    };
  });
  return { numericFields, excludedNumericFields, normalizedCandidates };
};

export const resolveRankingMetric = ({ rows, metadata }) => {
  const candidateModel = buildMetricCandidates(rows);
  const makeDiagnostics = ({ selected = null, ambiguityReason = null } = {}) => ({
    explicitRankingMetric: metadata?.explicitRankingMetric ?? null,
    explicitRankingMetricSource: metadata?.explicitRankingMetricSource ?? null,
    requestedMetric: metadata?.requestedMetric ?? null,
    requestedMetricSource: metadata?.requestedMetricSource ?? null,
    idsMetric: metadata?.idsMetric ?? null,
    idsMetricSource: metadata?.idsMetricSource ?? null,
    queryResultColumnsMetric: metadata?.queryResultColumnsMetric ?? null,
    queryResultColumnsMetricSource: metadata?.queryResultColumnsMetricSource ?? null,
    numericFields: candidateModel.numericFields,
    normalizedCandidates: candidateModel.normalizedCandidates.map(
      ({ canonical, fields, field, recognizedAdditive }) => ({
        canonical,
        fields,
        selectedField: field,
        recognizedAdditive,
      }),
    ),
    excludedNumericFields: candidateModel.excludedNumericFields,
    selectedCanonicalMetric: selected?.canonical ?? null,
    ambiguityReason,
  });
  const explicit = resolveMetricAlias(rows, metadata?.explicitRankingMetric);
  if (metadata?.explicitRankingMetric) {
    return {
      field: explicit,
      status: explicit ? "resolved" : "unresolved",
      source: metadata?.explicitRankingMetricSource || "ranking_metadata",
      diagnostics: makeDiagnostics({
        selected: explicit ? { canonical: getCanonicalMetric(explicit) } : null,
        ambiguityReason: explicit ? null : "explicit_metric_not_in_rows",
      }),
    };
  }
  const requested = resolveMetricAlias(rows, metadata?.requestedMetric);
  if (metadata?.requestedMetric) {
    return {
      field: requested,
      status: requested ? "resolved" : "unresolved",
      source: metadata?.requestedMetricSource || "requested_metric",
      diagnostics: makeDiagnostics({
        selected: requested ? { canonical: getCanonicalMetric(requested) } : null,
        ambiguityReason: requested ? null : "requested_metric_not_in_rows",
      }),
    };
  }
  // Soft fallbacks: unlike the explicit/requested metric above, an unresolved
  // ids/query_result_columns candidate does not block resolution — it just
  // falls through to the next source instead of forcing the ambiguity message.
  const idsMetricField = resolveMetricAlias(rows, metadata?.idsMetric);
  if (idsMetricField) {
    return {
      field: idsMetricField,
      status: "resolved",
      source: metadata?.idsMetricSource || "ids.metric.base_indicator",
      diagnostics: makeDiagnostics({
        selected: { canonical: getCanonicalMetric(idsMetricField) },
      }),
    };
  }
  const queryResultColumnsMetricField = resolveMetricAlias(rows, metadata?.queryResultColumnsMetric);
  if (queryResultColumnsMetricField) {
    return {
      field: queryResultColumnsMetricField,
      status: "resolved",
      source: metadata?.queryResultColumnsMetricSource || "query_result_columns",
      diagnostics: makeDiagnostics({
        selected: { canonical: getCanonicalMetric(queryResultColumnsMetricField) },
      }),
    };
  }

  const additiveCandidates = candidateModel.normalizedCandidates.filter(
    ({ recognizedAdditive }) => recognizedAdditive,
  );
  const selected = additiveCandidates.length === 1
    ? additiveCandidates[0]
    : additiveCandidates.length === 0 && candidateModel.normalizedCandidates.length === 1
      ? candidateModel.normalizedCandidates[0]
      : null;
  const ambiguityReason = selected
    ? null
    : additiveCandidates.length > 1
      ? "multiple_additive_metrics"
      : candidateModel.normalizedCandidates.length > 1
        ? "multiple_numeric_measures"
        : "no_valid_numeric_measure";
  return {
    field: selected?.field || null,
    status: selected ? "resolved" : "ambiguous",
    source: "inferred",
    candidates: candidateModel.normalizedCandidates.map(({ field }) => field),
    diagnostics: makeDiagnostics({ selected, ambiguityReason }),
  };
};

export const resolveRankField = (rows) => {
  const fieldByNormalized = new Map(
    collectFields(rows).map((field) => [normalizeToken(field), field]),
  );
  return RANK_FIELD_ALIASES.map((alias) => fieldByNormalized.get(alias)).find(Boolean) || null;
};

const formatObservationValue = (field, value) => {
  const normalized = normalizeToken(field);
  const text = String(value ?? "").trim();
  if (/reporting_date|(?:^|_)date$/.test(normalized) && /^\d{4}-\d{2}-\d{2}/.test(text)) {
    return text.slice(0, 10);
  }
  if (/(?:^|_)round(?:_number)?$/.test(normalized)) {
    return /^round\b/i.test(text) ? text : `Round ${text}`;
  }
  return text;
};

const getObservationCandidates = ({ rows, parentField, rankedEntityField, rankingMetric, rankField }) =>
  collectFields(rows).filter((field) => {
    if ([parentField, rankedEntityField, rankingMetric, rankField].includes(field)) return false;
    if (isInternalField(field) || isRankFieldName(field)) return false;
    if (isFiniteNumericField(rows, field) && !TEMPORAL_CONTEXT_PATTERN.test(normalizeToken(field))) {
      return false;
    }
    return rows.some((row) => isMeaningfulValue(row?.[field]) && isPrimitiveScalar(row?.[field]));
  });

const partitionRows = (rows, parentField) => {
  const partitions = new Map();
  rows.forEach((row) => {
    const key = parentField ? normalizeCategoryValue(row?.[parentField]) : "__overall";
    if (!partitions.has(key)) partitions.set(key, []);
    partitions.get(key).push(row);
  });
  return Array.from(partitions.values());
};

const fieldsIdentifyRows = (rows, parentField, fields) =>
  partitionRows(rows, parentField).every((partition) => {
    const identities = partition.map((row) => {
      const values = fields.map((field) => row?.[field]);
      if (values.some((value) => !isMeaningfulValue(value))) return null;
      return values.map((value) => normalizeCategoryValue(value)).join("|");
    });
    return identities.every(Boolean) && new Set(identities).size === identities.length;
  });

const resolveObservationFields = ({ rows, parentField, rankingMetric, rankField }) => {
  const candidates = getObservationCandidates({ rows, parentField, rankingMetric, rankField });
  const priority = (field) => {
    const normalized = normalizeToken(field);
    if (/observation|display_label|record_label/.test(normalized)) return 0;
    if (/reporting_date|(?:^|_)date$/.test(normalized)) return 1;
    if (/(?:^|_)round/.test(normalized)) return 2;
    if (/operation/.test(normalized)) return 3;
    if (/country|admin|region|origin|reason/.test(normalized)) return 4;
    if (/year/.test(normalized)) return 5;
    return 6;
  };
  const ordered = candidates.slice().sort((left, right) => priority(left) - priority(right));
  const single = ordered.find((field) => fieldsIdentifyRows(rows, parentField, [field]));
  if (single) return [single];
  for (let left = 0; left < ordered.length; left += 1) {
    for (let right = left + 1; right < ordered.length; right += 1) {
      const pair = [ordered[left], ordered[right]];
      if (fieldsIdentifyRows(rows, parentField, pair)) return pair;
    }
  }
  return [];
};

const getTooltipFieldDefinitions = ({
  rows,
  rankedEntityField,
  parentField,
  rankingMetric,
  metricFields = null,
  rankField,
  observationFields = [],
  labels,
}) => {
  const excludedMetricFields = metricFields && metricFields.length ? metricFields : [rankingMetric];
  return collectFields(rows)
    .filter((field) => {
      if ([rankedEntityField, parentField, rankField].includes(field)) return false;
      if (excludedMetricFields.includes(field)) return false;
      if (observationFields.includes(field)) return false;
      if (isInternalField(field) || isRankFieldName(field)) return false;
      const fixedValues = getMeaningfulUniqueValues(rows, field);
      const canonical = getCanonicalEntity(field);
      if (
        fixedValues.length === 1 &&
        (TEMPORAL_CONTEXT_PATTERN.test(normalizeToken(field)) ||
          ["country", "admin_1", "admin_2", "region", "operation"].includes(canonical))
      ) return false;
      return rows.some((row) => isMeaningfulValue(row?.[field]) && isPrimitiveScalar(row?.[field]));
    })
    .map((field) => ({ field, label: labels[field] || formatFieldLabel(field) }));
};

export const buildRankingTooltipFields = ({ row, definitions }) =>
  (definitions || []).flatMap(({ field, label }) => {
    const value = row?.[field];
    return isMeaningfulValue(value) ? [{ field, label, value }] : [];
  });

const getDisambiguationField = ({ rows, parentField, rankedEntityField, rankingMetric, rankField }) => {
  const duplicatesExist = partitionRows(rows, parentField).some((partition) => {
    const labels = partition.map((row) => normalizeCategoryValue(row?.[rankedEntityField]));
    return new Set(labels).size !== labels.length;
  });
  if (!duplicatesExist) return { duplicatesExist: false, field: null };
  const candidates = getObservationCandidates({
    rows,
    parentField,
    rankedEntityField,
    rankingMetric,
    rankField,
  });
  const field = candidates.find((candidate) =>
    partitionRows(rows, parentField).every((partition) => {
      const identities = partition.map(
        (row) =>
          `${normalizeCategoryValue(row?.[rankedEntityField])}|${normalizeCategoryValue(row?.[candidate])}`,
      );
      return identities.every((identity) => !identity.endsWith("|")) &&
        new Set(identities).size === identities.length;
    }),
  );
  return { duplicatesExist: true, field: field || null };
};

export const validateRankingRows = ({
  rows,
  parentField = null,
  rankedEntityField,
  rankingMetric,
  rankField = null,
}) => {
  if (!rankedEntityField) return { valid: false, warning: RANKING_ENTITY_UNRESOLVED_MESSAGE };
  const disambiguation = getDisambiguationField({
    rows,
    parentField,
    rankedEntityField,
    rankingMetric,
    rankField,
  });
  if (disambiguation.duplicatesExist && !disambiguation.field) {
    return { valid: false, warning: RANKING_DUPLICATE_MESSAGE };
  }
  return { valid: true, disambiguationField: disambiguation.field };
};

const buildChartRows = ({
  rows,
  rankedEntityField,
  observationFields,
  rankingMetric,
  rankField,
  parentField,
  disambiguationField,
  labels,
  tooltipDefinitions,
}) =>
  rows.map((row, index) => {
    const displayRank = isMeaningfulValue(row?.[rankField]) ? row[rankField] : index + 1;
    const baseEntityLabel = observationFields?.length
      ? observationFields.map((field) => formatObservationValue(field, row?.[field])).join(" · ")
      : String(row?.[rankedEntityField] ?? "");
    const entityLabel = disambiguationField
      ? `${baseEntityLabel} · ${formatObservationValue(disambiguationField, row?.[disambiguationField])}`
      : baseEntityLabel;
    return {
      label: `${displayRank}. ${entityLabel}`,
      __rankingValue: row?.[rankingMetric],
      __ranking: {
        entityLabel,
        displayRank,
        metricLabel: labels[rankingMetric] || formatFieldLabel(rankingMetric),
        metricValue: row?.[rankingMetric],
        parent: parentField
          ? {
              field: parentField,
              label: labels[parentField] || formatFieldLabel(parentField),
              value: row?.[parentField],
            }
          : null,
        tooltipFields: buildRankingTooltipFields({ row, definitions: tooltipDefinitions }),
      },
    };
  });

const buildSingleSeriesChartModel = (data, rankingMetric, labels) => ({
  data,
  series: [
    {
      value: rankingMetric,
      label: labels[rankingMetric] || formatFieldLabel(rankingMetric),
      identity: `ranking:${normalizeToken(rankingMetric)}`,
      dataKey: "__rankingValue",
      color: RANKING_PRIMARY_COLOR,
    },
  ],
  hasBreakdown: false,
  orientation: "horizontal",
  ranking: true,
});

export const buildRegularRankingModel = ({
  rows,
  rankedEntityField,
  rankingMetric,
  rankField = null,
  labels = {},
  metricFields = null,
}) => {
  const validation = validateRankingRows({ rows, rankedEntityField, rankingMetric, rankField });
  if (!validation.valid) return { status: "warning", warning: validation.warning };
  const tooltipDefinitions = getTooltipFieldDefinitions({
    rows,
    rankedEntityField,
    rankingMetric,
    metricFields,
    rankField,
    labels,
  });
  const data = buildChartRows({
    rows,
    rankedEntityField,
    rankingMetric,
    rankField,
    disambiguationField: validation.disambiguationField,
    labels,
    tooltipDefinitions,
  });
  return { status: "ready", chartModel: buildSingleSeriesChartModel(data, rankingMetric, labels) };
};

export const buildGroupedRankingModel = ({
  rows,
  parentField,
  rankedEntityField,
  observationFields = [],
  rankingMetric,
  rankField = null,
  selectedParent,
  labels = {},
  metricFields = null,
}) => {
  const parentOptions = getMeaningfulUniqueValues(rows, parentField).map((value) => ({
    value,
    key: normalizeCategoryValue(value),
    label: String(value),
  }));
  const selectedOption =
    parentOptions.find(({ key }) => key === normalizeCategoryValue(selectedParent)) ||
    parentOptions[0] ||
    null;
  const chartRows = selectedOption
    ? rows.filter(
        (row) => normalizeCategoryValue(row?.[parentField]) === selectedOption.key,
      )
    : [];
  const validation = observationFields.length
    ? { valid: fieldsIdentifyRows(chartRows, parentField, observationFields), disambiguationField: null }
    : validateRankingRows({
        rows: chartRows,
        parentField,
        rankedEntityField,
        rankingMetric,
        rankField,
      });
  if (!validation.valid) {
    return {
      status: "warning",
      warning: validation.warning || RANKING_FIGURE_UNRESOLVED_MESSAGE,
      parentOptions,
      selectedParent: selectedOption,
    };
  }
  const tooltipDefinitions = getTooltipFieldDefinitions({
    rows,
    rankedEntityField,
    parentField,
    rankingMetric,
    metricFields,
    rankField,
    observationFields,
    labels,
  });
  const data = buildChartRows({
    rows: chartRows,
    rankedEntityField,
    observationFields,
    rankingMetric,
    rankField,
    parentField,
    disambiguationField: validation.disambiguationField,
    labels,
    tooltipDefinitions,
  });
  return {
    status: "ready",
    parentOptions,
    selectedParent: selectedOption,
    chartRows,
    chartModel: buildSingleSeriesChartModel(data, rankingMetric, labels),
  };
};

const getContextValues = ({
  rows,
  rankedEntityField,
  parentField,
  rankingMetric,
  rankField,
  labels,
  includeParent,
}) =>
  collectFields(rows)
    .filter((field) => {
      if ([rankedEntityField, rankingMetric, rankField].includes(field)) return false;
      if (field === parentField) return includeParent;
      if (isInternalField(field) || isRankFieldName(field) || isFiniteNumericField(rows, field)) {
        return TEMPORAL_CONTEXT_PATTERN.test(normalizeToken(field));
      }
      const canonical = getCanonicalEntity(field);
      return ["country", "admin_1", "admin_2", "region", "operation"].includes(canonical) ||
        TEMPORAL_CONTEXT_PATTERN.test(normalizeToken(field));
    })
    .flatMap((field) => {
      const values = getMeaningfulUniqueValues(rows, field);
      return values.length === 1
        ? [{ field, label: labels[field] || formatFieldLabel(field), value: values[0] }]
        : [];
    })
    .slice(0, 6);

const getEntityCopy = (field, requested) => {
  const canonical = getCanonicalEntity(requested || field);
  if (canonical === "country") return "countries";
  if (canonical === "admin_1") return "Admin 1 areas";
  if (canonical === "admin_2") return "Admin 2 areas";
  if (canonical === "reason") return "reasons";
  if (canonical === "origin") return "origins";
  if (canonical === "observation") return "reported observations";
  return formatFieldLabel(field || requested || "entity").toLocaleLowerCase();
};

const getRankingTitle = ({ metadata, rankedEntityField, rankingMetric, labels, selectedParent }) => {
  const prefix = metadata.rankType === "bottom_n"
    ? "Bottom"
    : metadata.rankType === "top_n" ? "Top" : "Ranked";
  const entity = getEntityCopy(rankedEntityField, metadata.rankedEntity);
  if (selectedParent) return `${prefix} ${entity} in ${selectedParent.label} by ${labels[rankingMetric] || formatFieldLabel(rankingMetric)}`;
  return `${prefix} ${entity} by ${labels[rankingMetric] || formatFieldLabel(rankingMetric)}`;
};

const resolveAvailableMetricColumns = ({ response, rows, labels }) => {
  const columns = Array.isArray(response?.query_result_columns) ? response.query_result_columns : [];
  const metricColumns = columns.filter(
    (column) =>
      column &&
      typeof column === "object" &&
      normalizeToken(column.role) === "metric" &&
      column.key,
  );
  const seenFamilies = new Set();
  const resolved = [];
  metricColumns.forEach((column) => {
    const field = column.key;
    if (!isFiniteNumericField(rows, field)) return;
    const family = getCanonicalMetric(field);
    if (seenFamilies.has(family)) return;
    seenFamilies.add(family);
    resolved.push({
      family,
      field,
      label: labels?.[field] || column.label || formatFieldLabel(field),
    });
  });
  return resolved;
};

const resolveDefaultMetricField = ({ metadata, metricResolution, availableMetrics }) => {
  if (!availableMetrics.length) return metricResolution.field;
  const byFamily = new Map(availableMetrics.map(({ family, field }) => [family, field]));
  const explicitFamily = metadata?.explicitRankingMetric
    ? getCanonicalMetric(metadata.explicitRankingMetric)
    : null;
  if (explicitFamily && byFamily.has(explicitFamily)) return byFamily.get(explicitFamily);
  const resolvedFamily = metricResolution.field ? getCanonicalMetric(metricResolution.field) : null;
  if (resolvedFamily && byFamily.has(resolvedFamily)) return byFamily.get(resolvedFamily);
  return availableMetrics[0].field;
};

export const buildRankingPresentationModel = ({
  response,
  rows = response?.query_result,
  labels = {},
  selectedParent = null,
  activeMetric = null,
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (!isRankingResponse(response)) return { isRanking: false };
  const metadata = resolveRankingMetadata(response);
  const grouped = ["grouped_entity", "grouped_figure"].includes(metadata.rankLevel);
  const parentField = grouped ? resolveRankingParentField({ rows: safeRows, metadata }) : null;
  if (grouped && !parentField) {
    return { isRanking: true, mode: "grouped", status: "warning", warning: RANKING_PARENT_UNRESOLVED_MESSAGE, metadata };
  }
  const metricResolution = resolveRankingMetric({ rows: safeRows, metadata });
  if (!metricResolution.field) {
    return { isRanking: true, mode: grouped ? "grouped" : "regular", status: "warning", warning: RANKING_METRIC_UNRESOLVED_MESSAGE, metadata, metricResolution };
  }
  const availableMetrics = resolveAvailableMetricColumns({ response, rows: safeRows, labels });
  const defaultMetricField = resolveDefaultMetricField({ metadata, metricResolution, availableMetrics });
  const activeMetricField = availableMetrics.some(({ field }) => field === activeMetric)
    ? activeMetric
    : defaultMetricField;
  const metricFields = availableMetrics.length
    ? availableMetrics.map(({ field }) => field)
    : [activeMetricField];
  const rankField = resolveRankField(safeRows);
  let rankedEntityField = resolveRankedEntityField({ rows: safeRows, metadata, parentField });
  let observationFields = [];
  if (grouped && metadata.rankLevel === "grouped_figure" && !metadata.rankedEntity) {
    observationFields = resolveObservationFields({
      rows: safeRows,
      parentField,
      rankingMetric: activeMetricField,
      rankField,
    });
    if (!observationFields.length) {
      return { isRanking: true, mode: "grouped", status: "warning", warning: RANKING_FIGURE_UNRESOLVED_MESSAGE, metadata };
    }
    rankedEntityField = observationFields[0];
  }
  if (!rankedEntityField) {
    return { isRanking: true, mode: grouped ? "grouped" : "regular", status: "warning", warning: RANKING_ENTITY_UNRESOLVED_MESSAGE, metadata };
  }

  const common = {
    rows: safeRows,
    rankedEntityField,
    rankingMetric: activeMetricField,
    rankField,
    labels,
    metricFields,
  };
  const built = grouped
    ? buildGroupedRankingModel({
        ...common,
        parentField,
        observationFields,
        selectedParent,
      })
    : buildRegularRankingModel(common);
  const selectedParentOption = built.selectedParent || null;
  const contextValues = getContextValues({
    rows: grouped && built.chartRows?.length ? built.chartRows : safeRows,
    rankedEntityField,
    parentField,
    rankingMetric: activeMetricField,
    rankField,
    labels,
    includeParent: grouped && (built.parentOptions?.length || 0) === 1,
  });
  return {
    isRanking: true,
    mode: grouped ? "grouped" : "regular",
    metadata,
    parentField,
    rankedEntityField,
    observationFields,
    rankingMetric: activeMetricField,
    rankField,
    metricResolution,
    availableMetrics,
    defaultMetricField,
    parentOptions: built.parentOptions || [],
    selectedParent: selectedParentOption,
    chartRows: built.chartRows || safeRows,
    chartModel: built.chartModel || null,
    contextValues,
    status: built.status,
    warning: built.warning || null,
    title: getRankingTitle({
      metadata,
      rankedEntityField,
      rankingMetric: activeMetricField,
      labels,
      selectedParent: selectedParentOption,
    }),
  };
};

